// Google Workspace 監査のタブ枠。SF sf-audit.ts のミラー（サマリ/検出のロード）。
// 接続確認 → /gw/audit/run → toFindings → 描画。未接続 → CTA、失敗 → エラー + 再試行。
// connect タブは GW-SP1 の renderGoogleConnect に委譲。本 SP は AI・CSV・スナップショット等を含まない。
import { SECTION_TABS, type GwSection } from "./sections";
import { renderGoogleConnect } from "./connect";
import { renderGwSummary } from "./summary";
import { renderGwFindings } from "./findings";
import { renderGwChecksheet } from "./checksheet";
import { createGwAiAssistant } from "./ai-assistant";
import { analyze, buildFindingsSummary } from "./ai-api";
import { runGwAudit, toFindings } from "./audit-api";
import { listRuns, getRun } from "./history-api";
import { renderGwHistory } from "./history";
import { fetchActiveUsers } from "./user-reconcile-api";
import { renderGwUserReconcile } from "./user-reconcile";
import { getSchedule, putSchedule, deleteSchedule } from "./schedule-api";
import { renderGwSchedule } from "./schedule";
import { getConnection } from "./connect-api";
import { computeSeverityCounts } from "./audit-data";
import { renderNotConnected, renderLoadError } from "./empty-states";
import { createLoadingOverlay } from "../ui/loading-overlay";
import { showToast } from "../ui/toast";
import type { ApiClient } from "../api/client";

export interface GwAuditDeps {
  readonly section: GwSection;
  readonly onNavigate: (section: GwSection) => void;
  readonly apiClient?: ApiClient;
}

export function renderGwAudit(root: HTMLElement, deps: GwAuditDeps): void {
  root.replaceChildren();
  const tablist = document.createElement("div");
  tablist.setAttribute("role", "tablist");
  tablist.setAttribute("aria-label", "Google Workspace 監査セクション");
  tablist.className = "mb-6 flex flex-wrap gap-1 border-b border-slate-200";
  for (const tab of SECTION_TABS) {
    const btn = document.createElement("button"); btn.type = "button"; btn.setAttribute("role", "tab");
    const active = tab.section === deps.section;
    btn.setAttribute("aria-selected", active ? "true" : "false");
    btn.className = active
      ? "border-b-2 border-shinonome-700 px-3 py-2 text-sm font-medium text-shinonome-700"
      : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900";
    btn.textContent = tab.label;
    btn.addEventListener("click", () => deps.onNavigate(tab.section));
    tablist.append(btn);
  }
  const panel = document.createElement("div"); panel.setAttribute("role", "tabpanel");
  root.append(tablist, panel);

  if (deps.section === "connect") {
    renderGoogleConnect(panel, deps.apiClient !== undefined ? { apiClient: deps.apiClient } : {});
    return;
  }
  // チェックシートは「何をチェックするか」を接続前に知るための一覧なので、連携 CTA で隠さずカタログを見せる
  // （診断結果が無い項目は「未実施」として表示され、合格とは区別される）。
  if (deps.section === "checksheet" && deps.apiClient === undefined) { renderGwChecksheet(panel, {}); return; }
  if (deps.apiClient === undefined) { renderNotConnected(panel, deps.onNavigate); return; }

  const overlay = createLoadingOverlay({ message: "診断を読み込み中..." });
  panel.append(overlay.element); overlay.show();
  const apiClient = deps.apiClient;
  void (async () => {
    try {
      const conn = await getConnection({ apiClient });
      if (conn.status !== "connected") {
        overlay.hide();
        // 未接続でもチェックシートはカタログを見せる（全項目「未実施」）。診断は実行しない。
        if (deps.section === "checksheet") { renderGwChecksheet(panel, {}); return; }
        renderNotConnected(panel, deps.onNavigate);
        return;
      }
      if (deps.section === "history") {
        const runs = await listRuns({ apiClient });
        overlay.hide();
        renderGwHistory(panel, {
          runs,
          loadDetail: (id) => getRun({ apiClient }, id),
          onRunNow: async () => { await runGwAudit({ apiClient }, { persist: true }); renderGwAudit(root, deps); },
        });
        return;
      }
      if (deps.section === "user-reconcile") {
        const activeUsers = await fetchActiveUsers({ apiClient });
        overlay.hide();
        renderGwUserReconcile(panel, { activeUsers: activeUsers.map((u) => ({ email: u.email, active: true, lastLoginTime: u.lastLoginTime })) });
        return;
      }
      if (deps.section === "schedule") {
        const schedule = await getSchedule({ apiClient });
        overlay.hide();
        renderGwSchedule(panel, {
          schedule,
          // 保存/解除は SF sf-audit.ts のミラー。失敗（例: gw:diagnose RECONFIRM 403 / R12・R16）を
          // 握り潰さず toast で通知する。成功時のみ再描画（fail-closed 403 ではフォームを残す）。
          onSave: async (b) => {
            try {
              await putSchedule({ apiClient }, b);
              showToast({ tone: "success", title: "スケジュールを保存しました", message: "設定チェックを定期実行します。" });
              renderGwAudit(root, deps);
            } catch {
              showToast({ tone: "warning", title: "スケジュールの保存に失敗しました", message: "時間をおいて再度お試しください。" });
            }
          },
          onDisable: async () => {
            try {
              await deleteSchedule({ apiClient });
              showToast({ tone: "info", title: "スケジュールを解除しました", message: "定期実行を停止しました。" });
              renderGwAudit(root, deps);
            } catch {
              showToast({ tone: "warning", title: "スケジュールの解除に失敗しました", message: "時間をおいて再度お試しください。" });
            }
          },
        });
        return;
      }
      if (deps.section === "checksheet") {
        // 表示用の自動実行は read-only（persist を送らない＝ラン量産しない）。
        const resp = await runGwAudit({ apiClient });
        // notEvaluated は渡さない（coverage.notEvaluated は「カタログ − evaluated」の補集合なので、
        // 「findings に無い = 未評価」と同義。渡しても行は変わらない）。
        const { findings } = toFindings(resp);
        overlay.hide();
        renderGwChecksheet(panel, { findings });
        return;
      }
      const resp = await runGwAudit({ apiClient });
      const { findings, notEvaluated } = toFindings(resp);
      overlay.hide();
      // 実 Bedrock 連携（POST /gw/ai/analyze・東京 jp.）。サマリ/検出から AI に相談する右ドロワー。
      const assistant = createGwAiAssistant({ analyze: (summary, messages) => analyze({ apiClient }, { summary, messages }) });
      if (deps.section === "summary") {
        renderGwSummary(panel, {
          score: resp.healthCheckScore,
          findings,
          counts: computeSeverityCounts(findings),
          openAssistant: (ctx, sm) => assistant.open(ctx, sm ?? buildFindingsSummary(findings, resp.healthCheckScore)),
        });
      } else {
        renderGwFindings(panel, {
          findings,
          notEvaluated,
          openAssistant: (ctx) => assistant.open(ctx, buildFindingsSummary(findings, resp.healthCheckScore)),
        });
      }
      // renderGwSummary/renderGwFindings は panel を replaceChildren するため、その後に append する（drawer は fixed）。
      panel.append(assistant.element);
    } catch {
      overlay.hide();
      renderLoadError(panel, () => renderGwAudit(root, deps));
    }
  })();
}
